import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, MessageCircle, Video, Image as ImageIcon, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";

const STAGES = [
  ["delivery_before", "Before Delivery"],
  ["delivery_after", "After Delivery"],
  ["pickup_before", "Before Pickup"],
  ["pickup_after", "After Pickup"],
  ["inspection", "Inspection"],
];

export const MediaManager = ({ bookingId }) => {
  const [items, setItems] = useState([]);
  const [stage, setStage] = useState("delivery_before");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [followup, setFollowup] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/media", { params: { booking_id: bookingId } }).then((r) => setItems(r.data));
    api.get(`/media-followup/${bookingId}`, { params: { base: window.location.origin } }).then((r) => setFollowup(r.data)).catch(() => setFollowup(null));
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("booking_id", bookingId);
        fd.append("stage", stage);
        fd.append("notes", notes);
        fd.append("file", f);
        await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      load();
      toast.success(`${files.length} file(s) uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/media/${id}`); load(); toast.success("Removed");
  };

  return (
    <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="media-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold">Delivery, Pickup & Inspection Media</h2>
        {followup && (
          <a data-testid="media-followup-btn" href={followup.whatsapp_url} target="_blank" rel="noreferrer"
            className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            <MessageCircle size={15} /> Follow up on WhatsApp ({followup.count})
          </a>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {STAGES.map(([v, l]) => (
          <button key={v} data-testid={`media-stage-${v}`} onClick={() => setStage(v)}
            className={`text-xs px-3 py-1.5 rounded-full border ${stage === v ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
            {l} ({items.filter((m) => m.stage === v).length})
          </button>
        ))}
      </div>

      <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2 items-end">
        <Input data-testid="media-notes" placeholder="Optional note (e.g. tiles complete, small scratch on lid)"
          value={notes} onChange={(e) => setNotes(e.target.value)} />
        <label className={`inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm border border-black/15 cursor-pointer ${busy ? "opacity-50" : ""}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload photo / video
          <input ref={fileRef} data-testid="media-file-input" type="file" accept="image/*,video/*" multiple hidden disabled={busy} onChange={upload} />
        </label>
      </div>

      <div className="mt-4 space-y-4">
        {STAGES.map(([v, l]) => {
          const group = items.filter((m) => m.stage === v);
          if (!group.length) return null;
          return (
            <div key={v} data-testid={`media-group-${v}`}>
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">{l}</div>
              <div className="mt-2 flex flex-wrap gap-3">
                {group.map((m) => (
                  <div key={m.id} className="w-32" data-testid={`media-item-${m.id}`}>
                    <a href={`${API}${m.url.replace("/api", "")}`} target="_blank" rel="noreferrer" className="block">
                      {m.media_type === "video" ? (
                        <video src={`${API}${m.url.replace("/api", "")}`} className="w-32 h-24 object-cover rounded-md border border-black/10" muted />
                      ) : (
                        <img src={`${API}${m.url.replace("/api", "")}`} alt={m.notes} className="w-32 h-24 object-cover rounded-md border border-black/10" />
                      )}
                    </a>
                    <div className="mt-1 flex items-start justify-between gap-1">
                      <span className="text-[10px] text-ink-soft leading-tight flex items-center gap-1">
                        {m.media_type === "video" ? <Video size={10} /> : <ImageIcon size={10} />}{m.notes || m.media_type}
                      </span>
                      <button data-testid={`media-del-${m.id}`} onClick={() => remove(m.id)} className="text-ink-soft hover:text-lm-red"><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-ink-soft">No media yet. Upload before/after photos or videos so you have proof of condition.</p>}
      </div>

      {followup && (
        <pre className="mt-4 whitespace-pre-wrap font-sans text-xs text-ink-soft border-t border-black/5 pt-3" data-testid="media-followup-msg">{followup.message}</pre>
      )}
    </section>
  );
};
